import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Pause, Play, Square } from "lucide-react";
import { Button, Textarea } from "../ui";
import { useCoreDoc } from "../app/useCoreDoc";
import { readSettings } from "../app/settings";
import { useCircle } from "../circle/useCircle";
import { useDocSelector, useDocState } from "../core/useDocSelector";
import { useNow } from "../core/useNow";
import { readTask } from "../core/tasks";
import { setNotes } from "../core/ops";
import { fmtElapsed } from "../core/format";
import { elapsedMs, readCurrent, setTrackingScope, stopTracking } from "../tracking/timelog";
import {
  advanceIfDue,
  currentState,
  readPomodoro,
  remainingMs,
  startPomodoro,
  stopPomodoro,
} from "../tracking/pomodoro";

/**
 * Focus, large (`/focus`, or a task's focus with `?large=1`; spec §9.19 body doubling): whatever runs, large — the task's clock, the Pomodoro, or both
 * side by side — with the tracked task's notes below. Leaving it ("Return to main view", Escape) changes nothing:
 * the clocks go on.
 */
import { applyAutoPause, pauseTimer, resumeTimer, pausePom, resumePom } from "../tracking/linked";
export function BigClockPage({ onSmaller }: { onSmaller?: () => void } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = useNow();
  const { handle, workspaceId } = useCoreDoc();
  const { settled } = useDocState(handle);
  // outside the app shell: set the per-person timer scope of a shared space the way the shell does
  const circle = useCircle(handle);
  setTrackingScope(circle.shared ? circle.me : null);
  const current = useDocSelector(handle, (doc) => readCurrent(doc));
  const task = useDocSelector(handle, (doc) => {
    const cur = readCurrent(doc);
    return cur ? readTask(doc, cur.taskId) : undefined;
  });
  const pomodoro = useDocSelector(handle, (doc) => readPomodoro(doc));
  const pomSettings = useDocSelector(handle, (doc) => readSettings(doc).pomodoro);
  const [notes, setNotesDraft] = useState<string | null>(null);

  // the shell's background writes Pomodoro phase changes; this page is outside it, so it does it here
  useEffect(() => {
    if (!handle || handle.readOnly || !pomSettings) return;
    advanceIfDue(handle.doc, pomSettings, now);
    if (workspaceId && pomSettings.autoPauseInBreaks) void applyAutoPause(handle.doc, workspaceId, now);
  }, [handle, pomSettings, now, workspaceId]);

  // opened directly (a wall screen, a bookmark) there is no page to go back to: go home instead
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && back();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!handle || !settled) return <p className="p-8 text-neutral-500">{t("session.loading")}</p>;
  const ro = handle.readOnly;
  const pom = pomodoro && pomSettings ? currentState(pomodoro, pomSettings, now) : null;
  const both = !!current && !!pom;
  // on a phone held sideways the digits follow the height, so both clocks and their buttons fit
  const big = both ? "text-[4.5em] short:text-[18dvh]" : "text-[7em] short:text-[26dvh]";

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-5xl flex-col gap-8 px-4 py-10 mid:px-6 short:gap-4 short:py-4"
      data-testid="big-clock"
    >
      <div className={both ? "grid gap-8 short:gap-4 mid:grid-cols-2" : "grid gap-8"}>
        {current && (
          <section className="flex flex-col gap-3" data-testid="big-timer" data-paused={!!current.pausedAt}>
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
              {current.pausedAt ? t("bigClock.timerPaused") : t("bigClock.timer")}
            </p>
            <h1 className="text-[2em] font-semibold leading-tight">{task?.title || t("task.untitled")}</h1>
            <p
              className={`font-mono font-medium tabular-nums leading-none ${big} ${current.pausedAt ? "text-neutral-400" : ""}`}
              data-testid="big-timer-elapsed"
            >
              {fmtElapsed(elapsedMs(current, now))}
            </p>
            {!ro && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() =>
                    void (current.pausedAt ? resumeTimer(handle.doc, workspaceId!) : pauseTimer(handle.doc))
                  }
                  data-testid="big-timer-pause"
                >
                  {current.pausedAt ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
                  {current.pausedAt ? t("tracking.resume") : t("tracking.pause")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void stopTracking(handle.doc)}
                  data-testid="big-timer-stop"
                >
                  <Square size={13} aria-hidden /> {t("tracking.stop")}
                </Button>
              </div>
            )}
          </section>
        )}
        {pom && pomSettings && (
          <section className="flex flex-col gap-3" data-testid="big-pomodoro" data-paused={!!pom.pausedAt}>
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
              {pom.pausedAt ? t("bigClock.pomodoroPaused") : t("bigClock.pomodoro")}
            </p>
            <h2 className="text-[2em] font-semibold leading-tight">
              {t(`pomodoro.phase.${pom.phase}`)}{" "}
              <span className="text-[0.6em] font-normal text-neutral-500">
                {t("pomodoro.cycle", { n: pom.cycle })}
              </span>
            </h2>
            <p
              className={`font-mono font-medium tabular-nums leading-none ${big} ${pom.pausedAt ? "text-neutral-400" : ""}`}
              data-testid="big-pomodoro-remaining"
            >
              {fmtElapsed(remainingMs(pom, pomSettings, now))}
            </p>
            {!ro && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() =>
                    pom.pausedAt ? void resumePom(handle.doc, workspaceId!) : void pausePom(handle.doc)
                  }
                  data-testid="big-pomodoro-pause"
                >
                  {pom.pausedAt ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
                  {pom.pausedAt ? t("pomodoro.resume") : t("pomodoro.pause")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => stopPomodoro(handle.doc)}
                  data-testid="big-pomodoro-stop"
                >
                  <Square size={13} aria-hidden /> {t("pomodoro.stop")}
                </Button>
              </div>
            )}
          </section>
        )}
        {!current && !pom && (
          <section className="flex flex-col gap-3" data-testid="big-idle">
            <p className="text-[1.6em] text-neutral-500">{t("bigClock.nothing")}</p>
            {!ro && (
              <Button variant="secondary" className="w-fit" onClick={() => startPomodoro(handle.doc)}>
                {t("pomodoro.start")}
              </Button>
            )}
          </section>
        )}
      </div>

      {task && (
        <Textarea
          rows={8}
          aria-label={t("task.notes")}
          placeholder={t("notes.placeholder")}
          value={notes ?? task.notes}
          disabled={ro}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={() => {
            if (notes !== null) {
              setNotes(handle.doc, task.id, notes);
              setNotesDraft(null);
            }
          }}
          data-testid="big-notes"
        />
      )}

      <div className="mt-auto">
        {onSmaller && (
          <Button variant="secondary" onClick={onSmaller} data-testid="big-smaller">
            {t("bigClock.smaller")}
          </Button>
        )}
        <Button variant="secondary" onClick={back} data-testid="big-return">
          {t("bigClock.return")}
        </Button>
      </div>
    </div>
  );
}
